import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
	Heart,
	Search,
	MessageSquare,
	Calendar,
	HelpCircle,
	LogOut,
} from "lucide-react";

interface ParentLayoutProps {
	children: React.ReactNode;
}

const ParentLayout: React.FC<ParentLayoutProps> = ({ children }) => {
	const { signOut } = useAuth();
	const navigate = useNavigate();
	const [isScrolled, setIsScrolled] = useState(false);

	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > 10);
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	const handleSignOut = async () => {
		await signOut();
		navigate("/");
	};

	return (
		<div className="min-h-screen bg-gray-50 pb-20 lg:pb-0">
			{/* Sticky Glassy Header */}
			<nav
				className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
					isScrolled
						? "bg-white/80 backdrop-blur-md border-b border-gray-200 shadow-sm"
						: "bg-white/90 backdrop-blur-md border-b border-gray-200"
				}`}
			>
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="flex justify-between items-center h-16">
						{/* Logo and Clickable Dashboard Badge */}
						<div className="flex items-center">
							<div className="flex items-center space-x-2">
								<img
									src="https://d64gsuwffb70l.cloudfront.net/686337322e4231e379c159ce_1758159068714_b219be30.png"
									alt="Gaia"
									className="w-32 h-32 object-contain"
								/>
							</div>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => navigate("/parent-dashboard")}
								className="ml-3 px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full font-karla hover:bg-primary-200 transition-colors"
							>
								Parent Dashboard
							</Button>
						</div>

						{/* Desktop Navigation - Hidden on smaller screens */}
						<div className="hidden lg:flex items-center space-x-4">
							<Button
								variant="ghost"
								size="sm"
								onClick={() => navigate("/parent-favorites")}
								className="text-gray-700 hover:text-primary-500 hover:bg-primary-50"
							>
								<Heart className="h-4 w-4 mr-2" />
								Favorites
							</Button>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => navigate("/parent-messages")}
								className="text-gray-700 hover:text-primary-500 hover:bg-primary-50"
							>
								<MessageSquare className="h-4 w-4 mr-2" />
								Messages
							</Button>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => navigate("/parent-bookings")}
								className="text-gray-700 hover:text-primary-500 hover:bg-primary-50"
							>
								<Calendar className="h-4 w-4 mr-2" />
								Appointments
							</Button>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => navigate("/parent-faq")}
								className="text-gray-700 hover:text-primary-500 hover:bg-primary-50"
							>
								<HelpCircle className="h-4 w-4 mr-2" />
								FAQ
							</Button>
						</div>

						{/* Desktop Action Buttons - Hidden on smaller screens */}
						<div className="hidden lg:flex items-center space-x-4">
							<Button
								variant="outline"
								size="sm"
								onClick={handleSignOut}
								className="border-gray-300 text-gray-700 hover:bg-gray-50"
							>
								<LogOut className="h-4 w-4 mr-2" />
								Sign Out
							</Button>
							<Button
								onClick={() => navigate("/search-doulas")}
								className="bg-primary-500 hover:bg-primary-600 text-white shadow-lg"
							>
								<Search className="h-4 w-4 mr-2" />
								Find Doulas
							</Button>
						</div>

						{/* Mobile/Tablet Menu Button - Show on lg and below */}
						<div className="lg:hidden">
							<Button
								variant="ghost"
								size="sm"
								onClick={() => navigate("/search-doulas")}
								className="bg-primary-500 hover:bg-primary-600 text-white shadow-lg"
							>
								<Search className="h-4 w-4 mr-2" />
								Find Doulas
							</Button>
						</div>
					</div>
				</div>
			</nav>

			{/* Main Content */}
			<main className="pt-16">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
					{children}
				</div>
			</main>

			{/* Mobile/Tablet Bottom Navigation - Show on lg and below */}
			<nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 lg:hidden z-50">
				<div className="flex justify-around py-2">
					<Button
						variant="ghost"
						size="sm"
						className="flex-col h-16 px-2"
						onClick={() => navigate("/parent-favorites")}
					>
						<Heart className="h-5 w-5 mb-1" />
						<span className="text-xs">Favorites</span>
					</Button>
					<Button
						variant="ghost"
						size="sm"
						className="flex-col h-16 px-2"
						onClick={() => navigate("/parent-messages")}
					>
						<MessageSquare className="h-5 w-5 mb-1" />
						<span className="text-xs">Messages</span>
					</Button>
					<Button
						variant="ghost"
						size="sm"
						className="flex-col h-16 px-2"
						onClick={() => navigate("/parent-bookings")}
					>
						<Calendar className="h-5 w-5 mb-1" />
						<span className="text-xs">Appointments</span>
					</Button>
					<Button
						variant="ghost"
						size="sm"
						className="flex-col h-16 px-2"
						onClick={() => navigate("/parent-faq")}
					>
						<HelpCircle className="h-5 w-5 mb-1" />
						<span className="text-xs">FAQ</span>
					</Button>
				</div>

				{/* Mobile/Tablet Sign Out Button */}
				<div className="px-4 py-2 border-t border-gray-200">
					<Button
						variant="outline"
						size="sm"
						className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"
						onClick={handleSignOut}
					>
						<LogOut className="h-4 w-4 mr-2" />
						Sign Out
					</Button>
				</div>
			</nav>
		</div>
	);
};

export default ParentLayout;
